import {ReactNode} from "react";

export type OtsToggleOption<T extends string> = {
  value: T;
  label: ReactNode;
  /** Accessible name when the visible label is abbreviated, e.g. "Monday" for "Mon". */
  ariaLabel?: string;
  disabled?: boolean;
};

export type OtsToggleGroupProps<T extends string> = {
  options: OtsToggleOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  ariaLabel: string;
  disabled?: boolean;
  size?: "compact" | "default";
  /** Error state outlines the group; pair it with a message from OtsFormField. */
  invalid?: boolean;
  id?: string;
  className?: string;
};

/** A row of independent on/off pills for picking any number of options from a short list. */
export const OtsToggleGroup = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  size = "default",
  invalid = false,
  id,
  className,
}: OtsToggleGroupProps<T>) => {
  const toggle = (option: T) => {
    const next = value.includes(option) ? value.filter(item => item !== option) : [...value, option];
    // Keep the caller's option order so the emitted value is stable regardless of click order.
    onChange(options.map(item => item.value).filter(item => next.includes(item)));
  };
  return <div
    id={id}
    role="group"
    aria-label={ariaLabel}
    aria-invalid={invalid || undefined}
    className={[
      "inline-flex flex-wrap gap-1 rounded-lg",
      invalid ? "outline outline-1 outline-offset-2 outline-[var(--ots-error)]" : "",
      className,
    ].filter(Boolean).join(" ")}>
    {options.map(option => {
      const selected = value.includes(option.value);
      return <button
        key={option.value}
        type="button"
        aria-pressed={selected}
        aria-label={option.ariaLabel}
        disabled={disabled || option.disabled}
        onClick={() => toggle(option.value)}
        className={[
          "inline-flex items-center justify-center rounded-md border font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] disabled:cursor-not-allowed disabled:opacity-50",
          size === "compact" ? "min-w-9 px-2 py-1 text-xs" : "min-w-11 px-2.5 py-1.5 text-sm",
          selected
            ? "border-[var(--ots-primary)] bg-[var(--ots-primary)] text-[var(--ots-on-primary)]"
            : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text-muted)] hover:text-[var(--ots-text)]",
        ].join(" ")}>
        {option.label}
      </button>;
    })}
  </div>;
};
